"use client"

import OrgLayout, { useSidebar } from "@/layouts/OrgLayout"
import { useEffect, useState } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Plus, Trash2, Edit, Building2, Users, ChevronDown, X, Menu } from "lucide-react"
import { getCSRFToken } from "@/lib/utils"
import { createRefreshChannel } from "@/channels/refresh_channel"
import type { Department, Facility, User } from "@/types"

interface DepartmentsProps {
  departments: Department[]
  facilities: Facility[]
  users: User[]
}

export default function Departments({ departments: initialDepartments, facilities, users }: DepartmentsProps) {
  const [departments, setDepartments] = useState(initialDepartments || [])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Department | null>(null)
  const [searchQuery, setSearchQuery] = useState("")

  const [departmentName, setDepartmentName] = useState("")
  const [selectedFacilities, setSelectedFacilities] = useState<number[]>([])
  const [selectedUsers, setSelectedUsers] = useState<number[]>([])

  const [facilitySearch, setFacilitySearch] = useState("")
  const [userSearch, setUserSearch] = useState("")
  const [openDepartments, setOpenDepartments] = useState<Set<number>>(new Set())

  useEffect(() => {
    createRefreshChannel()
  }, [])

  // Prepopulate form when editing
  useEffect(() => {
    if (!editing) return
    setDepartmentName(editing.name)
    setSelectedFacilities(editing.facility_ids || [])
    setSelectedUsers(editing.user_ids || [])
  }, [editing])

  const resetForm = () => {
    setEditing(null)
    setDepartmentName("")
    setSelectedFacilities([])
    setSelectedUsers([])
    setFacilitySearch("")
    setUserSearch("")
  }

  const toggleSelection = (set: React.Dispatch<React.SetStateAction<number[]>>, arr: number[], id: number) => {
    set(arr.includes(id) ? arr.filter(i => i !== id) : [...arr, id])
  }

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const csrfToken = getCSRFToken()
    const payload = {
      department: {
        name: departmentName,
        facility_ids: selectedFacilities,
        user_ids: selectedUsers,
      },
    }
    const method = editing ? "PUT" : "POST"
    const url = editing ? `/department/${editing.id}` : "/department"

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (data.department) {
        setDepartments(prev =>
          editing ? prev.map(d => (d.id === editing.id ? data.department : d)) : [...prev, data.department]
        )
      }
    } catch (err) {
      console.error("Failed to save department:", err)
    } finally {
      setDialogOpen(false)
      resetForm()
    }
  }

  const removeDepartment = async (id: number) => {
    const csrfToken = getCSRFToken()
    try {
      const res = await fetch(`/departments/${id}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken || "" },
      })
      if (res.ok) {
        setDepartments(departments.filter(d => d.id !== id))
      }
    } catch (err) {
      console.error("Failed to delete department:", err)
    }
  }

  const filteredFacilities = facilities.filter(
    f => f.name?.toLowerCase().includes(facilitySearch.toLowerCase()) && !selectedFacilities.includes(f.id)
  )
  const filteredUsers = users.filter(
    u => u.email?.toLowerCase().includes(userSearch.toLowerCase()) && !selectedUsers.includes(u.id)
  )

  const filteredDepartments = departments.filter(d => d.name?.toLowerCase().includes(searchQuery.toLowerCase()))

  const toggleDepartment = (id: number) => {
    const newOpen = new Set(openDepartments)
    if (newOpen.has(id)) newOpen.delete(id)
    else newOpen.add(id)
    setOpenDepartments(newOpen)
  }

  const {setShowMenu} = useSidebar()
  return (
    <div className="space-y-6 w-full mt-2">
      <div className="flex items-center gap-4">
    <Menu className="h-6 w-6 ml-2 md:hidden  cursor-pointer" onClick={() => setShowMenu(true)} />
        <h2 className="text-2xl font-semibold">Departments</h2>
      </div>

      <div className="flex flex-col md:flex-row items-center gap-4">
        <div className="flex items-center gap-2 w-full">
          <Input
            placeholder="Search departments..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-2"
          />
        </div>

        <Dialog open={dialogOpen} onOpenChange={open => { setDialogOpen(open); if (!open) resetForm() }}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="w-full md:w-auto">
              <Plus className="h-4 w-4 mr-2" /> Add Department
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSave}>
              <DialogHeader>
                <DialogTitle>{editing ? "Edit Department" : "Add Department"}</DialogTitle>
                <DialogDescription>Set associated facilities and users for this department.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input value={departmentName} onChange={e => setDepartmentName(e.target.value)} required />
                </div>

                {/* Facilities selection */}
                <div className="space-y-2">
                  <Label>Facilities</Label>
                  {selectedFacilities.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-muted/30">
                      {selectedFacilities.map(id => {
                        const f = facilities.find(f => f.id === id)
                        return f && (
                          <Badge key={id} variant="secondary" className="gap-1">
                            {f.name}
                            <Button type="button" onClick={() => toggleSelection(setSelectedFacilities, selectedFacilities, id)} className="ml-1 hover:bg-destructive/20 rounded-full">
                              <X className="h-3 w-3" />
                            </Button>
                          </Badge>
                        )
                      })}
                    </div>
                  )}
                  <Input
                    placeholder="Search facilities..."
                    value={facilitySearch}
                    onChange={e => setFacilitySearch(e.target.value)}
                  />
                  {facilitySearch && filteredFacilities.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
                      {filteredFacilities.map(f => (
                        <Button key={f.id} type="button" onClick={() => toggleSelection(setSelectedFacilities, selectedFacilities, f.id)} className="w-full text-left px-3 py-2 hover:bg-accent text-sm">
                          {f.name}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Users selection */}
                <div className="space-y-2">
                  <Label>Users</Label>
                  {selectedUsers.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-muted/30">
                      {selectedUsers.map(id => {
                        const u = users.find(u => u.id === id)
                        return u && (
                          <Badge key={id} variant="secondary" className="gap-1">
                            {u.email}
                            <Button type="button" onClick={() => toggleSelection(setSelectedUsers, selectedUsers, id)} className="ml-1 hover:bg-destructive/20 rounded-full">
                              <X className="h-3 w-3" />
                            </Button>
                          </Badge>
                        )
                      })}
                    </div>
                  )}
                  <Input
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                  />
                  {userSearch && filteredUsers.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
                      {filteredUsers.map(u => (
                        <Button key={u.id} type="button" onClick={() => toggleSelection(setSelectedUsers, selectedUsers, u.id)} className="w-full text-left px-3 py-2 hover:bg-accent text-sm">
                          {u.email}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button type="submit">{editing ? "Update" : "Create Department"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-2">
        {filteredDepartments.map(dept => {
          const deptFacilities = facilities.filter(f => dept.facility_ids?.includes(f.id))
          const deptUsers = users.filter(u => dept.user_ids?.includes(u.id))

          return (
            <Collapsible key={dept.id} open={openDepartments.has(dept.id)} onOpenChange={() => toggleDepartment(dept.id)}>
              <Card className="p-4">
                <CollapsibleTrigger className="w-full">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <Building2 className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <p className="font-medium truncate">{dept.name}</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0 items-center">
                      <ChevronDown className={`h-4 w-4 transition-transform ${openDepartments.has(dept.id) ? "rotate-180" : ""}`} />
                      <Button variant="ghost" size="icon" onClick={e => { e.stopPropagation(); setEditing(dept); setDialogOpen(true) }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={e => { e.stopPropagation(); removeDepartment(dept.id) }}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <div className="pl-8 pt-2 space-y-2">
                    {deptFacilities.length > 0 && (
                      <div className="flex flex-wrap gap-1 items-center">
                        <span className="text-xs text-muted-foreground">Facilities:</span>
                        {deptFacilities.map(f => <Badge key={f.id} variant="outline" className="text-xs">{f.name}</Badge>)}
                      </div>
                    )}
                    {deptUsers.length > 0 && (
                      <div className="flex flex-wrap gap-1 items-center">
                        <span className="text-xs text-muted-foreground">Users:</span>
                        {deptUsers.map(u => <Badge key={u.id} variant="outline" className="text-xs">{u.email}</Badge>)}
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )
        })}
      </div>

      {filteredDepartments.length === 0 && (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground">No departments found matching your search.</p>
        </Card>
      )}
    </div>
  )
}

Departments.layout = (page: React.ReactNode) => <OrgLayout activeSection="departments">{page}</OrgLayout>

